<script setup>
import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue";

const allItems = ref([]);
const visibleItems = ref([]);
const isLoading = ref(true);
const error = ref("");
const selectedItem = ref(null);
const selectedBrand = ref("All");
const searchQuery = ref("");
const showBackToTop = ref(false);
const favouriteIds = ref([]);
const showQuoteForm = ref(false);
const quoteName = ref("");
const quoteEmail = ref("");
const quoteDetails = ref("");
const quoteStatus = ref("");
const isSubmittingQuote = ref(false);
const showBundleQuoteForm = ref(false);
const bundleQuoteName = ref("");
const bundleQuoteEmail = ref("");
const bundleQuoteDetails = ref("");
const bundleQuoteStatus = ref("");
const isSubmittingBundleQuote = ref(false);
const bundleItemComments = ref({});
const toastMessage = ref("");
let toastTimer;

const batchSize = 24;
const isAppending = ref(false);
const favouritesStorageKey = "bumblefox-library-favourites";

const brands = computed(() => {
  const uniqueBrands = [...new Set(allItems.value.map((item) => item.brand))];
  return ["All", "Favourites", ...uniqueBrands.sort((a, b) => a.localeCompare(b))];
});

const favouriteItems = computed(() =>
    allItems.value.filter((item) => favouriteIds.value.includes(item.id))
);

const filteredItems = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  let items = allItems.value;

  if (selectedBrand.value === "All") {
    items = allItems.value;
  } else if (selectedBrand.value === "Favourites") {
    items = allItems.value.filter((item) => favouriteIds.value.includes(item.id));
  } else {
    items = allItems.value.filter((item) => item.brand === selectedBrand.value);
  }

  if (!query) {
    return items;
  }

  return items.filter((item) => item.name.toLowerCase().includes(query));
});

function resetVisibleItems() {
  visibleItems.value = filteredItems.value.slice(0, batchSize);
}

async function loadMore() {
  if (isAppending.value) {
    return;
  }

  if (visibleItems.value.length >= filteredItems.value.length) {
    return;
  }

  isAppending.value = true;

  const nextItems = filteredItems.value.slice(
      visibleItems.value.length,
      visibleItems.value.length + batchSize
  );

  visibleItems.value = [...visibleItems.value, ...nextItems];
  isAppending.value = false;
}

function handleScroll() {
  const scrollTop = window.scrollY || window.pageYOffset;
  const windowHeight = window.innerHeight;
  const fullHeight = document.documentElement.scrollHeight;

  showBackToTop.value = scrollTop > 400;

  if (scrollTop + windowHeight >= fullHeight - 300) {
    loadMore();
  }
}

function openOverlay(item) {
  selectedItem.value = item;
  showQuoteForm.value = false;
  quoteStatus.value = "";
}

function closeOverlay() {
  selectedItem.value = null;
  showQuoteForm.value = false;
}

function openQuoteForm() {
  showQuoteForm.value = true;
  quoteStatus.value = "";
}

function showToast(message) {
  toastMessage.value = message;
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    toastMessage.value = "";
  }, 4500);
}

async function submitQuote() {
  if (!selectedItem.value || isSubmittingQuote.value) return;

  isSubmittingQuote.value = true;
  quoteStatus.value = "";

  const body = new URLSearchParams({
    "form-name": "library-quote-request",
    "request-type": "Single item quote",
    "bot-field": "",
    name: quoteName.value,
    email: quoteEmail.value,
    item: `${selectedItem.value.name} — ${selectedItem.value.brand}`,
    "item-id": String(selectedItem.value.id),
    details: quoteDetails.value,
  });

  try {
    const response = await fetch("/", {
      method: "POST",
      headers: {"Content-Type": "application/x-www-form-urlencoded"},
      body: body.toString(),
    });

    if (!response.ok) throw new Error(`Quote request failed: ${response.status}`);

    quoteName.value = "";
    quoteEmail.value = "";
    quoteDetails.value = "";
    quoteStatus.value = "";
    closeOverlay();
    showToast("Thanks! Your quote request has been sent.");
  } catch (err) {
    console.error(err);
    quoteStatus.value = "Sorry, we couldn't send your request. Please try again.";
  } finally {
    isSubmittingQuote.value = false;
  }
}

function openBundleQuoteForm() {
  showBundleQuoteForm.value = true;
  bundleQuoteStatus.value = "";
}

async function submitBundleQuote() {
  if (!favouriteItems.value.length || isSubmittingBundleQuote.value) return;

  isSubmittingBundleQuote.value = true;
  bundleQuoteStatus.value = "";
  const bundleItems = favouriteItems.value
      .map((item) => {
        const comment = bundleItemComments.value[item.id]?.trim();
        return `${item.name} — ${item.brand} (ID: ${item.id})${comment ? `\n  Item note: ${comment}` : ""}`;
      })
      .join("\n");
  const body = new URLSearchParams({
    "form-name": "library-quote-request",
    "request-type": "Bundle quote",
    "bot-field": "",
    name: bundleQuoteName.value,
    email: bundleQuoteEmail.value,
    item: `${favouriteItems.value.length} favourite items`,
    "item-id": favouriteItems.value.map((item) => item.id).join(", "),
    "bundle-items": bundleItems,
    details: bundleQuoteDetails.value,
  });

  try {
    const response = await fetch("/", {
      method: "POST",
      headers: {"Content-Type": "application/x-www-form-urlencoded"},
      body: body.toString(),
    });

    if (!response.ok) throw new Error(`Bundle quote request failed: ${response.status}`);

    bundleQuoteName.value = "";
    bundleQuoteEmail.value = "";
    bundleQuoteDetails.value = "";
    bundleItemComments.value = {};
    bundleQuoteStatus.value = "";
    showBundleQuoteForm.value = false;
    showToast("Thanks! Your bundle quote request has been sent.");
  } catch (err) {
    console.error(err);
    bundleQuoteStatus.value = "Sorry, we couldn't send your request. Please try again.";
  } finally {
    isSubmittingBundleQuote.value = false;
  }
}

function selectBrand(brand) {
  selectedBrand.value = brand;
  resetVisibleItems();
  window.scrollTo({top: 0, behavior: "smooth"});
}

function scrollToTop() {
  window.scrollTo({top: 0, behavior: "smooth"});
}

function loadFavourites() {
  try {
    const saved = localStorage.getItem(favouritesStorageKey);
    favouriteIds.value = saved ? JSON.parse(saved) : [];
  } catch (err) {
    console.error("Failed to load favourites", err);
    favouriteIds.value = [];
  }
}

function saveFavourites() {
  localStorage.setItem(
      favouritesStorageKey,
      JSON.stringify(favouriteIds.value)
  );
}

function isFavourite(itemId) {
  return favouriteIds.value.includes(itemId);
}

function toggleFavourite(item) {
  if (isFavourite(item.id)) {
    favouriteIds.value = favouriteIds.value.filter((id) => id !== item.id);
  } else {
    favouriteIds.value = [...favouriteIds.value, item.id];
  }

  saveFavourites();

  if (selectedBrand.value === "Favourites") {
    resetVisibleItems();
  }
}

watch(filteredItems, () => {
  resetVisibleItems();
});

onMounted(async () => {
  try {
    loadFavourites();

    const res = await fetch("/library.json");
    const thangsRes = await fetch("/thangs-library.json");
    const patreonRes = await fetch("/patreon-library.json");

    if (!res.ok || !thangsRes.ok || !patreonRes.ok) {
      throw new Error(`Failed to load library: ${res.status}`);
    }


    const mmfItems = await res.json();
    const thangsItems = await thangsRes.json();
    const patreonItems = await patreonRes.json();
    allItems.value = [...mmfItems, ...thangsItems, ...patreonItems]
        .sort((a, b) => {
              const licenceAddedOnComp = b.licenceAddedOn.localeCompare(a.licenceAddedOn);

              return (licenceAddedOnComp === 0)? b.created.localeCompare(a.created): licenceAddedOnComp;
            }
        );
    resetVisibleItems();

    window.addEventListener("scroll", handleScroll, {passive: true});
  } catch (err) {
    console.error(err);
    error.value = "Sorry, we couldn't load the library right now.";
  } finally {
    isLoading.value = false;
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleScroll);
  window.clearTimeout(toastTimer);
});
</script>

<template>
  <section id="library" class="library-section">
    <Transition name="toast">
      <div v-if="toastMessage" class="quote-toast" role="status" aria-live="polite">
        <span aria-hidden="true">✓</span>
        {{ toastMessage }}
        <button type="button" aria-label="Dismiss notification" @click="toastMessage = ''">×</button>
      </div>
    </Transition>

    <h2>Library</h2>

    <div class="library-description">
      <p>
        Checkout our full library from our fantastic licenced designers! If
        there's anything you see here you'd love to see in our store then please
        get in touch =).
      </p>
      <i class="library-note">*Note: Colors may vary If you see something you love, please contact us to check if it's
        available or coming soon!*</i>
    </div>

    <p v-if="isLoading" class="library-message">Loading library...</p>
    <p v-else-if="error" class="library-message">{{ error }}</p>

    <template v-else>
      <div class="library-filters">
        <label class="filter-search">
          <span class="sr-only">Search model names</span>
          <input
              v-model="searchQuery"
              type="search"
              placeholder="Search model names"
              aria-label="Search model names"
          />
        </label>

        <button
            class="filter-button favourites-filter-button"
            :class="{ active: selectedBrand === 'Favourites' }"
            @click="selectBrand('Favourites')"
        >
          Favourites
          <span v-if="favouriteIds.length">
            ({{ favouriteIds.length }})
          </span>
        </button>

        <label class="filter-select">
          <span>Filter by designer</span>
          <select
              :value="selectedBrand === 'Favourites' ? 'All' : selectedBrand"
              @change="selectBrand($event.target.value)"
          >
            <option v-for="brand in brands.filter((brand) => brand !== 'Favourites')" :key="brand" :value="brand">
              {{ brand }}
            </option>
          </select>
        </label>
      </div>

      <section
          v-if="selectedBrand === 'Favourites' && favouriteItems.length"
          class="bundle-quote"
          aria-labelledby="bundle-quote-heading"
      >
        <h3 id="bundle-quote-heading">Make your favourites a bundle</h3>
        <p>Request one quote for all {{ favouriteItems.length }} saved item{{ favouriteItems.length === 1 ? "" : "s" }}.</p>
        <button
            v-if="!showBundleQuoteForm"
            class="quote-toggle-button"
            type="button"
            @click="openBundleQuoteForm"
        >
          Request a bundle quote
        </button>
        <form v-else class="quote-form" @submit.prevent="submitBundleQuote">
          <input type="hidden" name="form-name" value="library-quote-request" />
          <button
              class="bundle-quote-close"
              type="button"
              aria-label="Collapse bundle quote request"
              @click="showBundleQuoteForm = false"
          >
            ×
          </button>
          <label>
            Your name
            <input v-model.trim="bundleQuoteName" autocomplete="name" required />
          </label>
          <label>
            Email address
            <input v-model.trim="bundleQuoteEmail" type="email" autocomplete="email" required />
          </label>
          <label>
            Size, colour, quantity or other details
            <textarea v-model.trim="bundleQuoteDetails" rows="3"></textarea>
          </label>
          <p class="bundle-quote-hint">You can add a separate note to each saved item below.</p>
          <button class="quote-submit-button" type="submit" :disabled="isSubmittingBundleQuote">
            {{ isSubmittingBundleQuote ? "Sending…" : "Send bundle quote request" }}
          </button>
        </form>
        <p v-if="bundleQuoteStatus" class="quote-status" role="status">{{ bundleQuoteStatus }}</p>
      </section>

      <div class="library-grid">
        <article
            v-for="item in visibleItems"
            :key="item.id"
            class="library-card"
            @click="openOverlay(item)"
        >
          <button
              class="favourite-button"
              :class="{ active: isFavourite(item.id) }"
              @click.stop="toggleFavourite(item)"
              :aria-label="isFavourite(item.id) ? 'Remove from favourites' : 'Add to favourites'"
          >
            {{ isFavourite(item.id) ? "♥" : "♡" }}
          </button>

          <img
              :src="item.imageUrl"
              :alt="item.name"
              class="library-image"
              loading="lazy"
          />

          <div class="library-card-content">
            <h3>{{ item.name }}</h3>
            <p class="library-brand">By {{ item.brand }}</p>
          </div>

          <label
              v-if="selectedBrand === 'Favourites' && showBundleQuoteForm"
              class="bundle-item-comment"
              @click.stop
          >
            Note for this item
            <textarea
                v-model="bundleItemComments[item.id]"
                rows="2"
                placeholder="e.g. blue, large, quantity 2"
                @click.stop
            ></textarea>
          </label>
        </article>
      </div>

      <p
          v-if="visibleItems.length < filteredItems.length"
          class="library-message"
      >
        Scroll down to load more...
      </p>

      <p
          v-else-if="filteredItems.length === 0"
          class="library-message"
      >
        No models found for this search and filter selection.
      </p>
    </template>

    <div
        v-if="selectedItem"
        class="library-overlay"
        @click="closeOverlay"
    >
      <div
          class="library-overlay-content"
          @click.stop
      >
        <button
            class="library-overlay-close"
            @click="closeOverlay"
            aria-label="Close image preview"
        >
          ×
        </button>

        <button
            class="overlay-favourite-button"
            :class="{ active: isFavourite(selectedItem.id) }"
            @click.stop="toggleFavourite(selectedItem)"
            :aria-label="isFavourite(selectedItem.id) ? 'Remove from favourites' : 'Add to favourites'"
        >
          {{ isFavourite(selectedItem.id) ? "♥" : "♡" }}
        </button>

        <img
            :src="selectedItem.imageUrl"
            :alt="selectedItem.name"
            class="library-overlay-image"
        />

        <div class="library-overlay-text">
          <h3>{{ selectedItem.name }}</h3>
          <p>By {{ selectedItem.brand }}</p>

          <button
              v-if="!showQuoteForm"
              class="quote-toggle-button"
              type="button"
              @click="openQuoteForm"
          >
            Request a quote
          </button>

          <form
              v-else
              class="quote-form"
              name="library-quote-request"
              method="POST"
              data-netlify="true"
              @submit.prevent="submitQuote"
          >
            <input type="hidden" name="form-name" value="library-quote-request" />
            <h4>Request a quote for {{ selectedItem.name }}</h4>
            <label>
              Your name
              <input v-model.trim="quoteName" name="name" autocomplete="name" required />
            </label>
            <label>
              Email address
              <input v-model.trim="quoteEmail" name="email" type="email" autocomplete="email" required />
            </label>
            <label>
              Size, colour, quantity or other details
              <textarea v-model.trim="quoteDetails" name="details" rows="3"></textarea>
            </label>
            <button class="quote-submit-button" type="submit" :disabled="isSubmittingQuote">
              {{ isSubmittingQuote ? "Sending…" : "Send quote request" }}
            </button>
          </form>
          <p v-if="quoteStatus" class="quote-status" role="status">{{ quoteStatus }}</p>
        </div>
      </div>
    </div>

    <button
        v-if="showBackToTop"
        class="back-to-top-button"
        @click="scrollToTop"
        aria-label="Back to top"
    >
      ↑ Top
    </button>
  </section>
</template>

<style scoped>
.library-section {
  padding: 40px 20px;
}

.quote-toast {
  position: fixed;
  top: 92px;
  right: 20px;
  z-index: 10001;
  display: flex;
  align-items: center;
  gap: 9px;
  max-width: min(360px, calc(100vw - 40px));
  box-sizing: border-box;
  padding: 14px 15px;
  border: 1px solid rgba(46, 45, 97, 0.16);
  border-radius: 12px;
  background: #fff8ef;
  box-shadow: 0 10px 30px rgba(46, 45, 97, 0.2);
  color: #2e2d61;
  font-weight: 700;
  text-align: left;
}

.quote-toast > span {
  display: grid;
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 50%;
  background: #2e2d61;
  color: white;
}

.quote-toast button {
  margin-left: auto;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-size: 1.35rem;
  line-height: 1;
}

.toast-enter-active,
.toast-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.library-description {
  max-width: 800px;
  margin: 0 auto 20px;
  text-align: center;
  line-height: 1.6;
}

.library-note {
  color: #777;
  font-size: 0.9rem;
}

.library-message {
  text-align: center;
  margin-top: 20px;
}

.bundle-quote {
  max-width: 620px;
  margin: 26px auto 0;
  padding: 20px;
  border-radius: 12px;
  background: #fff8e8;
  text-align: center;
}

.bundle-quote h3,
.bundle-quote p {
  margin: 0 0 8px;
}

.bundle-quote-hint {
  color: #555;
  font-size: 0.9rem;
  text-align: center;
}

.bundle-quote-close {
  float: right;
  margin: -8px -4px 0 0;
  border: 0;
  background: transparent;
  color: #2e2d61;
  cursor: pointer;
  font-size: 1.7rem;
  line-height: 1;
}

.library-filters {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin: 30px auto 10px;
  max-width: 1000px;
}

.filter-search input {
  border: 1px solid #d9d9d9;
  border-radius: 999px;
  padding: 10px 14px;
  min-width: 220px;
  font-size: 0.95rem;
}

.filter-select {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #2e2d61;
  font-size: 0.9rem;
  font-weight: 700;
}

.filter-select select {
  border: 1px solid #d9d9d9;
  border-radius: 999px;
  padding: 10px 34px 10px 14px;
  background: white;
  color: #2c3e50;
  font: inherit;
}

.filter-select select:focus {
  outline: none;
  border-color: #2e2d61;
  box-shadow: 0 0 0 2px rgba(46, 45, 97, 0.12);
}

.filter-search input:focus {
  outline: none;
  border-color: #999;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.08);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.filter-button {
  border: none;
  border-radius: 999px;
  padding: 10px 16px;
  background: #f3f3f3;
  cursor: pointer;
  font-size: 0.95rem;
  transition: background 0.2s ease,
  transform 0.2s ease;
}

.filter-button:hover {
  background: #e7e7e7;
  transform: translateY(-1px);
}

.filter-button.active {
  background: #222;
  color: white;
}

.library-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 250px));
  gap: 20px;
  justify-content: center;
  margin-top: 30px;
}

.library-card {
  position: relative;
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s ease,
  box-shadow 0.2s ease;
  cursor: pointer;
}

.library-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.12);
}

.favourite-button,
.overlay-favourite-button {
  border: none;
  cursor: pointer;
  font-size: 1.4rem;
  line-height: 1;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 999px;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.favourite-button {
  position: absolute;
  top: 10px;
  left: 10px; /* 👈 move to right */
  z-index: 2;
}

.overlay-favourite-button {
  position: absolute;
  top: 14px;
  left: 54px;
  z-index: 2;
}

.favourite-button.active,
.overlay-favourite-button.active {
  color: #d14b7d;
}

.library-image {
  display: block;
  width: 100%;
  height: 220px;
  object-fit: cover;
}

.library-card-content {
  padding: 12px;
}

.bundle-item-comment {
  display: block;
  padding: 0 12px 12px;
  color: #2e2d61;
  font-size: 0.85rem;
  font-weight: 700;
  text-align: left;
}

.bundle-item-comment textarea {
  display: block;
  box-sizing: border-box;
  width: 100%;
  margin-top: 5px;
  border: 1px solid #ccc;
  border-radius: 7px;
  padding: 8px;
  font: inherit;
  resize: vertical;
}

.bundle-item-comment textarea::placeholder {
  color: #a8a8a8;
  opacity: 1;
}

.library-card-content h3 {
  margin: 0 0 8px;
  font-size: 1rem;
  line-height: 1.4;
}

.library-brand {
  margin: 0;
  color: #555;
  font-size: 0.9rem;
}

.library-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 100px 20px 20px;
  z-index: 9999;
}

.library-overlay-content {
  position: relative;
  background: white;
  border-radius: 16px;
  max-width: 900px;
  max-height: 90vh;
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
  overflow: auto;
}

.library-overlay-close {
  position: absolute;
  top: 10px;
  right: 14px;
  border: none;
  background: transparent;
  font-size: 2rem;
  line-height: 1;
  cursor: pointer;
}

.library-overlay-image {
  display: block;
  max-width: 100%;
  max-height: 70vh;
  margin: 0 auto;
  border-radius: 10px;
  object-fit: contain;
}

.library-overlay-text {
  text-align: center;
  margin-top: 16px;
}

.library-overlay-text h3 {
  margin: 0 0 8px;
}

.library-overlay-text p {
  margin: 0;
  color: #555;
}

.quote-toggle-button,
.quote-submit-button {
  margin-top: 16px;
  border: 0;
  border-radius: 999px;
  padding: 11px 20px;
  background: #222;
  color: white;
  cursor: pointer;
  font: inherit;
}

.quote-form {
  max-width: 520px;
  margin: 18px auto 0;
  text-align: left;
}

.quote-form h4 {
  margin: 0 0 12px;
  text-align: center;
}

.quote-form label {
  display: block;
  margin-top: 10px;
  font-weight: 600;
}

.quote-form input,
.quote-form textarea {
  display: block;
  box-sizing: border-box;
  width: 100%;
  margin-top: 5px;
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 10px;
  font: inherit;
}

.quote-form input:focus,
.quote-form textarea:focus {
  outline: 2px solid #222;
  outline-offset: 2px;
}

.quote-submit-button {
  display: block;
  margin-right: auto;
  margin-left: auto;
}

.quote-submit-button:disabled {
  cursor: wait;
  opacity: 0.65;
}

.library-overlay-text .quote-status {
  margin-top: 12px;
  color: #245c35;
  font-weight: 600;
}

.back-to-top-button {
  position: fixed;
  right: 20px;
  bottom: 100px;
  z-index: 9998;
  border: none;
  border-radius: 999px;
  padding: 12px 16px;
  background: #222;
  color: white;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
  font-size: 0.95rem;
  transition: transform 0.2s ease,
  background 0.2s ease;
}

.back-to-top-button:hover {
  background: #444;
  transform: translateY(-2px);
}

@media (max-width: 600px) {
  .quote-toast {
    top: 112px;
    right: 12px;
    max-width: calc(100vw - 24px);
  }

  .library-section {
    padding: 28px 12px;
  }

  .library-filters {
    align-items: stretch;
    margin-top: 22px;
  }

  .filter-search,
  .filter-search input,
  .filter-select,
  .filter-select select {
    box-sizing: border-box;
    width: 100%;
  }

  .filter-select {
    justify-content: space-between;
  }

  .filter-select select {
    width: auto;
    max-width: 65%;
  }

  .library-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-top: 20px;
  }

  .library-image {
    height: 150px;
  }

  .library-card-content {
    padding: 10px;
  }

  .library-card-content h3 {
    font-size: 0.9rem;
  }

  .favourite-button {
    top: 7px;
    left: 7px;
    width: 36px;
    height: 36px;
  }

  .library-overlay {
    padding: 70px 12px 12px;
  }

  .library-overlay-content {
    padding: 16px;
  }
}
</style>
